/**
 * Best Sellers — tabs + drag-to-scroll rail + progress bar + nav arrows.
 * All three layouts (v1/v2/v3) share the same DOM; only card width and
 * rail gap differ. One init handles all three.
 */
(function () {
  'use strict';

  /* Every root on the page, not just the first: the homepage can carry Our Picks
     AND the Fall Sale rail, which reuse this markup. The file can also be loaded
     by both sections, so each root is wired once only. 2026-10-06. */
  function init(root) {
  if (root.hasAttribute('data-bs-ready')) return;
  root.setAttribute('data-bs-ready', '');

  /* A sale rail carries its end time. Liquid already stops rendering it after the
     end, but a page opened before then (or served from a cache) would keep
     advertising a finished sale. */
  var endsAt = parseInt(root.getAttribute('data-sale-ends') || '0', 10);
  if (endsAt && Date.now() / 1000 > endsAt) {
    root.hidden = true;
    return;
  }
  /* ...and a page left open across the end time hides it when the time comes.
     setTimeout cannot wait longer than about 24 days, so further out it is left
     to the next page load. */
  if (endsAt) {
    var msLeft = endsAt * 1000 - Date.now();
    if (msLeft < 2147483647) setTimeout(function () { root.hidden = true; }, msLeft);
  }

  /* In room rails lead with each card's lifestyle photo. Mark a photo ready only
     once it has decoded, so the CSS never reveals an empty frame. Lazy photos in
     a hidden tab load when that tab opens and mark themselves then. */
  if (root.getAttribute('data-img-mode') === 'room') {
    root.querySelectorAll('[data-card-lifestyle-img]').forEach(function (img) {
      if (img.hasAttribute('data-lf-ready')) return;
      var ds = img.getAttribute('data-src');
      if (ds && !img.getAttribute('src')) {
        var dss = img.getAttribute('data-srcset');
        if (dss) { img.setAttribute('srcset', dss); img.removeAttribute('data-srcset'); }
        img.setAttribute('src', ds);
        img.removeAttribute('data-src');
      }
      if (img.complete && img.naturalWidth > 0) img.setAttribute('data-lf-ready', '');
      else img.addEventListener('load', function () { img.setAttribute('data-lf-ready', ''); }, { once: true });
    });
  }

  var tabs = root.querySelectorAll('.bestsellers-tab');
  var carousels = root.querySelectorAll('.bestsellers-scroll');
  /* Scoped to this root. Our Picks keeps its old id, a second rail uses the data
     attribute, so two rails never drive each other's bar. */
  var progressBar = root.querySelector('[data-bestsellers-progress], #bestsellers-progress');
  var prevBtn = root.querySelector('.bestsellers-nav-prev');
  var nextBtn = root.querySelector('.bestsellers-nav-next');
  /* Which tab starts active is a theme setting now, so read it from the markup
     Liquid painted rather than assuming Best Sellers leads. Getting this wrong
     is not cosmetic: the click handler early-returns when the clicked tab
     already matches activeTab, so a stale assumption makes the leading tab
     completely dead until you click the other one first. */
  var initialRail = root.querySelector('.bestsellers-scroll:not(.hidden)');
  var activeTab = initialRail
    ? initialRail.getAttribute('data-carousel')
    : 'best-sellers';

  function getActiveCarousel() {
    for (var i = 0; i < carousels.length; i++) {
      if (carousels[i].getAttribute('data-carousel') === activeTab) return carousels[i];
    }
    return null;
  }

  // Tab switching
  tabs.forEach(function (tab) {
    tab.addEventListener('click', function () {
      var target = tab.getAttribute('data-tab');
      if (target === activeTab) return;
      activeTab = target;

      tabs.forEach(function (t) {
        var line = t.querySelector('.bestsellers-tab-line');
        t.setAttribute('aria-selected', t.getAttribute('data-tab') === activeTab ? 'true' : 'false');
        if (t.getAttribute('data-tab') === activeTab) {
          t.classList.remove('text-brand-text-muted');
          t.classList.add('text-foreground');
          if (line) line.classList.remove('hidden');
        } else {
          t.classList.remove('text-foreground');
          t.classList.add('text-brand-text-muted');
          if (line) line.classList.add('hidden');
        }
      });

      carousels.forEach(function (c) {
        if (c.getAttribute('data-carousel') === activeTab) {
          c.classList.remove('hidden');
          c.scrollLeft = 0;
          updateProgress(c);
          syncControls();
        } else {
          c.classList.add('hidden');
        }
      });
    });
  });

  // Progress bar
  function updateProgress(el) {
    if (!el || !progressBar) return;
    var max = el.scrollWidth - el.clientWidth;
    var pct = max > 0 ? (el.scrollLeft / max) * 100 : 0;
    progressBar.style.width = Math.max(20, pct) + '%';
  }

  carousels.forEach(function (c) {
    var ticking = false;
    c.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(function () {
          if (!c.classList.contains('hidden')) updateProgress(c);
          ticking = false;
        });
      }
    }, { passive: true });
  });

  // Nav arrows — scroll active rail by one card width
  function scrollCarousel(dir) {
    var c = getActiveCarousel();
    if (!c) return;
    var card = c.querySelector('.snap-start');
    var amount = card ? card.offsetWidth + 16 : 300;
    c.scrollBy({ left: dir === 'prev' ? -amount : amount, behavior: 'smooth' });
  }

  /* A tab whose cards all fit has nothing to scroll to. A Next that does
     nothing and a bar parked at 20% suggest products that are not there (the
     15% tab of the Fall Sale rail, 2 cards, 2026-10-06). Hide both while that
     tab is open; visibility, not display, so the layout does not jump. */
  var progressTrack = progressBar ? progressBar.parentElement : null;
  function syncControls() {
    var c = getActiveCarousel();
    var scrollable = !!c && c.scrollWidth > c.clientWidth + 1;
    [prevBtn, nextBtn, progressTrack].forEach(function (el) {
      if (el) el.style.visibility = scrollable ? '' : 'hidden';
    });
  }
  syncControls();
  var resizeTick = false;
  window.addEventListener('resize', function () {
    if (resizeTick) return;
    resizeTick = true;
    requestAnimationFrame(function () { resizeTick = false; syncControls(); });
  });

  if (prevBtn) prevBtn.addEventListener('click', function () { scrollCarousel('prev'); });
  if (nextBtn) nextBtn.addEventListener('click', function () { scrollCarousel('next'); });

  // Drag-to-scroll
  carousels.forEach(function (c) {
    var isDragging = false;
    var startX = 0;
    var startScrollLeft = 0;

    c.addEventListener('mousedown', function (e) {
      isDragging = true;
      startX = e.pageX;
      startScrollLeft = c.scrollLeft;
      c.classList.remove('cursor-grab');
      c.classList.add('cursor-grabbing', 'select-none');
    });

    c.addEventListener('mousemove', function (e) {
      if (!isDragging) return;
      e.preventDefault();
      var dx = e.pageX - startX;
      c.scrollLeft = startScrollLeft - dx;
    });

    function stopDrag() {
      if (!isDragging) return;
      isDragging = false;
      c.classList.remove('cursor-grabbing', 'select-none');
      c.classList.add('cursor-grab');
    }

    c.addEventListener('mouseup', stopDrag);
    c.addEventListener('mouseleave', stopDrag);

    c.addEventListener('click', function (e) {
      if (Math.abs(e.pageX - startX) > 5) e.preventDefault();
    }, true);
  });
  }

  document.querySelectorAll('[data-bestsellers-root]').forEach(init);
})();
